import {
  OligoAwareFolderItemType,
  SequenceSchemaType,
  TagSchemaSupportedFolderItemType,
} from 'constants/RegistryConstants';
import {
  isRequiredLinkSchemaInterface,
  isRequiredLinkTagSchema,
  isRequiredLinkFolderItemType,
  isRequiredLinkAnyEntity,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaField, FieldType} from 'schemas/fields/SchemaFieldConstants';
import {oligoAwareFolderItemTypeMatchesTagSchema} from 'tag-schema/util/TagSchemaUtil';
import {assertNever} from 'util/Types';

// Schema field link target types ordered from most to least specific.
export enum LinkTargetType {
  SCHEMA = 'SCHEMA',
  SCHEMA_INTERFACE = 'SCHEMA_INTERFACE',
  MODEL_TYPE = 'MODEL_TYPE',
  ANY_ENTITY = 'ANY_ENTITY',
}

interface SchemaLinkTarget {
  type: LinkTargetType.SCHEMA;
  schemaId: string;
  modelType: OligoAwareFolderItemType;
}
interface SchemaInterfaceLinkTarget {
  type: LinkTargetType.SCHEMA_INTERFACE;
  schemaInterfaceId: string;
}
interface ModelTypeLinkTarget {
  type: LinkTargetType.MODEL_TYPE;
  modelType: OligoAwareFolderItemType;
}

interface AnyEntityLinkTarget {
  type: LinkTargetType.ANY_ENTITY;
}

type FieldLinkTarget =
  | SchemaLinkTarget
  | SchemaInterfaceLinkTarget
  | ModelTypeLinkTarget
  | AnyEntityLinkTarget;

export function maybeGetPolymerCompatibleLinkTarget(
  field: SchemaField,
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType>
): FieldLinkTarget | null {
  const requiredLink = field.requiredLink;
  if (field.fieldType !== FieldType.BIOENTITY_LINK || requiredLink == null) {
    return null;
  }

  if (isRequiredLinkSchemaInterface(requiredLink)) {
    // Note: in theory there are many schema interfaces that can be excluded, but Schemas does not type
    // `itemType` in the requiredLink shape currently.
    return {
      type: LinkTargetType.SCHEMA_INTERFACE,
      schemaInterfaceId: requiredLink.schemaInterface.id,
    };
  } else if (isRequiredLinkTagSchema(requiredLink)) {
    const tagSchema = requiredLink.tagSchema;
    if (tagSchema.sequenceType === SequenceSchemaType.MIXED) {
      return null;
    }
    for (const supportedOligoAwareFolderItemType of oligoAwareFolderItemTypes) {
      // Since MIXED schemas are excluded, there should be at most one match.
      if (oligoAwareFolderItemTypeMatchesTagSchema(supportedOligoAwareFolderItemType, tagSchema)) {
        return {
          type: LinkTargetType.SCHEMA,
          schemaId: tagSchema.id,
          modelType: supportedOligoAwareFolderItemType,
        };
      }
    }
    return null;
  } else if (isRequiredLinkFolderItemType(requiredLink)) {
    const modelType = requiredLink.folderItemType as TagSchemaSupportedFolderItemType;

    // Note: It appears that you can only have a generic link to long DNA sequences, no oligos or long RNA.
    if (oligoAwareFolderItemTypes.includes(modelType)) {
      return {
        type: LinkTargetType.MODEL_TYPE,
        modelType,
      };
    }
    return null;
  } else if (isRequiredLinkAnyEntity(requiredLink)) {
    return {
      type: LinkTargetType.ANY_ENTITY,
    };
  } else {
    return null;
  }
}

// Shared structure for polymer counts
export interface PolymerCounts {
  unschematizedPolymerCounts: number;
  polymerCountsBySchemaId: {[schemaId: string]: number};
}

// Structure for single model type entities (constructs, primers)
export interface AssemblyEntityCounts extends PolymerCounts {
  modelType: OligoAwareFolderItemType;
}

export interface RawOutputPolymerCount {
  type: 'RAW_OUTPUT_POLYMER_COUNT';
  modelType: OligoAwareFolderItemType;
  count: number;
}

export interface UnknownInputPolymers {
  type: 'UNKNOWN_INPUT_POLYMERS';
  polymerIds: Array<string>;
}

// Structure for multi-model type entities (fragments)
export type AssemblyEntityCountsByModelType = {[modelType in OligoAwareFolderItemType]?: PolymerCounts};

export interface FragmentDetails {
  inputDetails: UnknownInputPolymers;
  outputDetails: RawOutputPolymerCount;
}

export type SchemaIdToImplementedInterfaces = Record<string, Set<string>>;

/**
 * Merges multiple AssemblyEntityCounts objects into one.
 *
 * All input objects must have the same modelType.
 * The function combines the unschematizedPolymerCounts and merges the polymerCountsBySchemaId maps.
 *
 * @param details Array of AssemblyEntityCounts objects to merge
 * @returns A single merged AssemblyEntityCounts object
 * @throws Error if the input array is empty or contains objects with different modelTypes
 */
export function mergeAssemblyEntityCounts(details: Array<AssemblyEntityCounts>): AssemblyEntityCounts {
  if (details.length === 0) {
    throw new Error('Cannot merge an empty array of AssemblyEntityCounts');
  }

  // Use the first object's modelType as the reference
  const modelType = details[0].modelType;

  // Ensure all objects have the same modelType
  for (let i = 1; i < details.length; i++) {
    if (details[i].modelType !== modelType) {
      throw new Error('Cannot merge AssemblyEntityCounts with different modelTypes');
    }
  }

  // Start with zero counts
  let mergedUnschematizedPolymerCounts = 0;
  const mergedPolymerCountsBySchemaId: {[schemaId: string]: number} = {};

  // Merge each object's data
  for (const detail of details) {
    // Sum unschematized polymer counts
    mergedUnschematizedPolymerCounts += detail.unschematizedPolymerCounts;

    // Sum polymer counts by schema ID
    for (const [schemaId, count] of Object.entries(detail.polymerCountsBySchemaId)) {
      mergedPolymerCountsBySchemaId[schemaId] = (mergedPolymerCountsBySchemaId[schemaId] || 0) + count;
    }
  }

  return {
    modelType,
    unschematizedPolymerCounts: mergedUnschematizedPolymerCounts,
    polymerCountsBySchemaId: mergedPolymerCountsBySchemaId,
  };
}

export function getFieldLinkCompatibilityCounts(
  schemaField: SchemaField,
  entityDetails: AssemblyEntityCounts,
  schemaIdToImplementedInterfaces: SchemaIdToImplementedInterfaces
): {compatibleCount: number; totalCount: number} {
  let compatibleCount = 0;

  // Calculate total count for the single model type
  let totalCount = entityDetails.unschematizedPolymerCounts;
  for (const count of Object.values(entityDetails.polymerCountsBySchemaId)) {
    totalCount += count;
  }

  const linkTarget = maybeGetPolymerCompatibleLinkTarget(
    schemaField,
    [entityDetails.modelType] // Pass the single modelType as an array
  );

  if (!linkTarget) {
    return {compatibleCount: 0, totalCount};
  }

  switch (linkTarget.type) {
    case LinkTargetType.SCHEMA: {
      // Check if the schema target matches our model type
      if (linkTarget.modelType === entityDetails.modelType) {
        const count = entityDetails.polymerCountsBySchemaId[linkTarget.schemaId];
        compatibleCount = count || 0;
      }
      break;
    }

    case LinkTargetType.SCHEMA_INTERFACE: {
      // Check schemas that implement the interface
      for (const [schemaId, count] of Object.entries(entityDetails.polymerCountsBySchemaId)) {
        const implementedInterfaces = schemaIdToImplementedInterfaces[schemaId];
        if (implementedInterfaces && implementedInterfaces.has(linkTarget.schemaInterfaceId)) {
          compatibleCount += count;
        }
      }
      break;
    }

    case LinkTargetType.MODEL_TYPE: {
      // Check if the model type matches
      if (linkTarget.modelType === entityDetails.modelType) {
        compatibleCount = entityDetails.unschematizedPolymerCounts;
        for (const count of Object.values(entityDetails.polymerCountsBySchemaId)) {
          compatibleCount += count;
        }
      }
      break;
    }

    case LinkTargetType.ANY_ENTITY: {
      compatibleCount = totalCount;
      break;
    }

    default:
      assertNever(linkTarget);
  }

  return {compatibleCount, totalCount};
}

/**
 * Merges multiple AssemblyEntityCountsByModelType objects into one.
 * Combines counts for each model type across all inputs.
 */
export function mergeAssemblyEntityCountsByModelType(
  details: Array<AssemblyEntityCountsByModelType>
): AssemblyEntityCountsByModelType {
  const mergedCountsByModelType: {[modelType in OligoAwareFolderItemType]?: PolymerCounts} = {};

  for (const detail of details) {
    for (const [modelType, counts] of Object.entries(detail)) {
      const currentModelType = modelType as OligoAwareFolderItemType;

      if (!mergedCountsByModelType[currentModelType]) {
        mergedCountsByModelType[currentModelType] = {
          unschematizedPolymerCounts: 0,
          polymerCountsBySchemaId: {},
        };
      }

      const merged = mergedCountsByModelType[currentModelType];
      merged.unschematizedPolymerCounts += counts.unschematizedPolymerCounts;

      for (const [schemaId, count] of Object.entries(counts.polymerCountsBySchemaId)) {
        merged.polymerCountsBySchemaId[schemaId] = (merged.polymerCountsBySchemaId[schemaId] || 0) + count;
      }
    }
  }

  return mergedCountsByModelType;
}

/**
 * Calculates the total count of all polymers across all model types.
 */
export function getTotalCountFromAssemblyEntityCountsByModelType(
  entityDetails: AssemblyEntityCountsByModelType
): number {
  let totalCount = 0;

  for (const counts of Object.values(entityDetails)) {
    totalCount +=
      counts.unschematizedPolymerCounts +
      Object.values(counts.polymerCountsBySchemaId).reduce((sum, count) => sum + count, 0);
  }

  return totalCount;
}

/**
 * Gets field link compatibility counts for multi-model type entities.
 * Aggregates counts across all model types that are compatible with the field.
 */
export function getFieldLinkCompatibilityCountsMultiModel(
  schemaField: SchemaField,
  entityDetails: AssemblyEntityCountsByModelType,
  schemaIdToImplementedInterfaces: SchemaIdToImplementedInterfaces
): {compatibleCount: number; totalCount: number} {
  let compatibleCount = 0;

  // Use the helper function to calculate total count
  const totalCount = getTotalCountFromAssemblyEntityCountsByModelType(entityDetails);

  // Process each model type in the multi-model structure
  for (const [modelType, counts] of Object.entries(entityDetails)) {
    const currentModelType = modelType as OligoAwareFolderItemType;

    // Create a single-model type object to reuse existing logic
    const singleModelDetails: AssemblyEntityCounts = {
      modelType: currentModelType,
      unschematizedPolymerCounts: counts.unschematizedPolymerCounts,
      polymerCountsBySchemaId: counts.polymerCountsBySchemaId,
    };

    // Get compatibility for this model type
    const {compatibleCount: modelCompatibleCount} = getFieldLinkCompatibilityCounts(
      schemaField,
      singleModelDetails,
      schemaIdToImplementedInterfaces
    );

    compatibleCount += modelCompatibleCount;
  }

  return {compatibleCount, totalCount};
}
